import React, { useMemo, useState } from 'react';
import { Container, Header, ColumnLayout, Box, Select, SpaceBetween } from '@amzn/awsui-components-react-v3';
import StackedBars from './StackedBars';
import StuckFilterBar from './StuckFilterBar';
import TrendStrip from './TrendStrip';
import TicketVerificationTable from './TicketVerificationTable';
import PlatformStatesTable from './PlatformStatesTable';
import { StuckRow, StuckFilters, HistoryPoint, Region, TicketInfo } from './types';
import { STUCK_FACETS, REGIONS, STUCK_TICKET_THRESHOLD } from './constants';
import { applyFilters, buildTrends, distinctValues, toCsv, downloadCsv } from './helpers';
import { buildStacked } from './stuckLayout';

interface ViewProps {
    filters: StuckFilters;
    contextLabel: string;
    allRows: StuckRow[];
    history: HistoryPoint[];
    tickets: TicketInfo[];
    setFacet: (key: string, values: string[]) => void;
    clearFacets: () => void;
}

const facetDefs = STUCK_FACETS.map(([k, l]) => [k, l, k] as [string, string, string]);

// Selectable chart dimension (mirrors the local "Stuck count by [dim]" dropdown).
const DIM_OPTIONS = [
    { label: 'Plugin', value: 'plugin' },
    { label: 'Operation', value: 'op' },
    { label: 'State (full)', value: 'state' },
    { label: 'Workflow', value: 'workflow' },
    { label: 'Rail', value: 'rail' },
];

const dimFnFor = (dim: string) => (r: StuckRow): string => {
    if (dim === 'op') return r.op && r.op !== '—' ? r.op : 'Native';
    return String(r[dim as keyof StuckRow] ?? '(none)');
};

const StuckWorkflowView: React.FC<ViewProps> = ({ filters, contextLabel, allRows, history, tickets, setFacet, clearFacets }) => {
    const [dim, setDim] = useState(DIM_OPTIONS[0]);

    // Per-region model: backend returns the deployed region's rows; honor facet filters.
    const rows = useMemo(() => applyFilters(allRows, filters, STUCK_FACETS), [allRows, filters]);
    const trends = useMemo(() => buildTrends(history), [history]);
    const historyNow = history.length ? history[history.length - 1].totalStuck : 0;

    const regionsPresent = useMemo(() => REGIONS.filter((r) => rows.some((x) => x.region === r)), [rows]);

    const stacked = useMemo(
        () => buildStacked(rows, dimFnFor(dim.value)),
        [rows, dim]
    );

    const facetOptions = (key: string) => {
        const field = STUCK_FACETS.find(([k]) => k === key)?.[2] as keyof StuckRow;
        return distinctValues(allRows, field);
    };

    const byRegion = useMemo(() => {
        const m: Partial<Record<Region, number>> = {};
        rows.forEach((r) => { m[r.region] = (m[r.region] || 0) + r.stuck; });
        return m;
    }, [rows]);
    const total = useMemo(() => rows.reduce((s, r) => s + r.stuck, 0), [rows]);

    const onCsv = () => {
        const csv = toCsv(
            ['Region', 'Workflow', 'Rail', 'Plugin', 'Operation', 'State', 'Stuck'],
            rows as unknown as Array<Record<string, unknown>>,
            ['region', 'workflow', 'rail', 'plugin', 'op', 'state', 'stuck']
        );
        downloadCsv('sherlock-stuck-workflows.csv', csv);
    };

    return (
        <SpaceBetween size="l">
            <StuckFilterBar
                filters={filters}
                facetDefs={facetDefs}
                facetOptions={facetOptions}
                regionLabel={contextLabel}
                setFacet={setFacet}
                clearFacets={clearFacets}
                onCsv={onCsv}
            />

            <Container header={<Header variant="h2">Overview</Header>}>
                <ColumnLayout columns={1 + regionsPresent.length} variant="text-grid">
                    <div>
                        <Box variant="awsui-key-label">Total stuck (error-state)</Box>
                        <Box fontSize="display-l" fontWeight="bold" color={total > 0 ? 'text-status-warning' : 'inherit'}>{total.toLocaleString()}</Box>
                    </div>
                    {regionsPresent.map((r) => (
                        <div key={r}>
                            <Box variant="awsui-key-label">{r}</Box>
                            <Box fontSize="display-l" fontWeight="bold">{(byRegion[r] || 0).toLocaleString()}</Box>
                        </div>
                    ))}
                </ColumnLayout>
            </Container>

            <TrendStrip trends={trends} currentTotal={historyNow} />

            <StackedBars
                header={
                    <Header
                        variant="h2"
                        actions={
                            <Select
                                selectedOption={dim}
                                onChange={({ detail }) => setDim(detail.selectedOption as { label: string; value: string })}
                                options={DIM_OPTIONS}
                                ariaLabel="Chart dimension"
                            />
                        }
                    >
                        Stuck count by {dim.label} (stacked by region)
                    </Header>
                }
                entries={stacked}
                threshold={STUCK_TICKET_THRESHOLD}
                regionsPresent={regionsPresent}
            />

            <TicketVerificationTable rows={rows} tickets={tickets} />
            <PlatformStatesTable rows={rows} />
        </SpaceBetween>
    );
};

export default StuckWorkflowView;
export type { ViewProps };
